import { HttpParams } from '@angular/common/http';

function splitUrl(url: string): { baseUrl: string; queryString: string; hash: string; hasQueryParameters: boolean } {
  const [urlWithoutHash, ...hashParts] = url.split('#');
  const [baseUrl, ...queryParts] = urlWithoutHash.split('?');

  return {
    baseUrl,
    queryString: queryParts.join('?'),
    hash: hashParts.length ? `#${hashParts.join('#')}` : '',
    hasQueryParameters: queryParts.length > 0
  };
}

export default function encodeUrlParams(url?: string): string {
  if (!url) {
    return url;
  }

  try {
    const urlParts = url.split('?');
    const hasQueryParameters = urlParts.length > 1;

    if (!hasQueryParameters) {
      return url;
    }

    const baseUrl = urlParts[0];
    const queryString = urlParts.slice(1).join('?');

    const decodedQueryString = decodeURIComponent(queryString);
    if (decodedQueryString !== queryString) {
      return url;
    }

    const params = new HttpParams({ fromString: queryString });
    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    return url;
  }
}

export function getQueryParamIgnoreCase(url: string, paramName: string): string {
  if (!url) {
    return null;
  }

  try {
    const {queryString, hasQueryParameters} = splitUrl(url);
    if (!hasQueryParameters) {
      return null;
    }

    const params = new URLSearchParams(queryString);
    const match = Array.from(params.keys()).find(key => key.toLowerCase() === paramName.toLowerCase());
    return match ? params.get(match) : null;
  } catch (error) {
    return null;
  }
}

export function removeQueryParamsIgnoreCase(url: string, paramNames: string[]): string {
  if (!url) {
    return url;
  }

  try {
    const {baseUrl, queryString, hash, hasQueryParameters} = splitUrl(url);
    if (!hasQueryParameters) {
      return url;
    }

    const params = new URLSearchParams(queryString);
    const normalizedNames = paramNames.map(paramName => paramName.toLowerCase());
    Array.from(params.keys())
      .filter(key => normalizedNames.includes(key.toLowerCase()))
      .forEach(key => params.delete(key));

    const updatedQueryString = params.toString();
    return `${baseUrl}${updatedQueryString ? `?${updatedQueryString}` : ''}${hash}`;
  } catch (error) {
    return url;
  }
}

export function setQueryParamIgnoreCase(url: string, paramName: string, value: string): string {
  if (!url || value === null || value === undefined || value === '') {
    return url;
  }

  try {
    const {baseUrl, queryString, hash} = splitUrl(url);
    const params = new URLSearchParams(queryString);
    Array.from(params.keys())
      .filter(key => key.toLowerCase() === paramName.toLowerCase())
      .forEach(key => params.delete(key));
    params.set(paramName, value);

    return `${baseUrl}?${params.toString()}${hash}`;
  } catch (error) {
    return url;
  }
}
